import React from 'react'
import styled from 'styled-components'
import { MEDIUM, TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import { Swipeable } from 'react-swipeable'
import FeatureCard from '~shared/FeatureCard'
import Container from '~shared/Container'
import Topic from '~shared/Topic'
import PrestyledLink from '~shared/Link'
import Stops from '~shared/Stops'
import SliderNav from '~shared/SliderNav'
import Spacer from '~shared/Spacer'
import Display from '~shared/Display'
import urls from '~utils/urls'

const Apps = styled.div`
    display: grid;
    margin: 4.5rem 2rem 0;
    grid-template-columns: repeat(4, 280px);
    grid-column-gap: 1.5rem;
    grid-row-gap: 1.5rem;
    transition: 300ms transform ease-in-out;
    width: fit-content;

    ${FeatureCard} {
        position: relative;

        ::after {
            content: '';
            position: absolute;
            width: 100%;
            height: 100%;
            border-radius: 8px;
            box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08);
            transition: opacity 300ms ease-in-out;
            top: 0;
            left: 0;
            opacity: 0;
            pointer-events: none;
        }

        :hover {
            ::after {
                opacity: 1;
            }
        }
    }

    @media (min-width: ${TABLET}px) {
        transition: none;
        transform: none !important;
        margin: 6.5rem auto 0;
        grid-template-columns: repeat(2, 336px);
        grid-column-gap: 2rem;
        grid-row-gap: 2rem;
    }

    @media (min-width: ${DESKTOP}px) {
        margin: 6.5rem auto 0;
        width: fit-content;
        grid-template-columns: repeat(4, 280px);
        grid-column-gap: 1rem;
        grid-row-gap: 1rem;
    }
`

const Link = styled(PrestyledLink)`
    color: #0421ff !important;
    font-weight: ${MEDIUM};
`

const UnstyledExternalLink = ({ children, ...props }) => (
    <Link {...props} newTab noDecoration onClick={(e) => e.stopPropagation()}>
        {children}
        <LinkIcon />
    </Link>
)

const ExternalLink = styled(UnstyledExternalLink)`
    svg {
        margin-left: 0.5rem;
        width: 8px;
        height: 8px;
    }
`

const UnstyledBullet = (props) => (
    <span {...props}>•</span>
)

const Bullet = styled(UnstyledBullet)`
    margin: 0 0.75rem;
`

const LinkIcon = () => (
    <svg viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.253.7H7.07v5.818" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path stroke="currentColor" strokeLinecap="round" d="M7.071.707L.707 7.071" />
    </svg>
)

const UnstyledApplicationLayer = (props) => (
    <div {...props}>
        <Topic.Title $appear>
            Discover the Streamr application layer
        </Topic.Title>
        <Topic.Body>
            <Container>
                <Topic.Paragraph $appear>
                    Truly decentralised innovation with real-time data applications is enabled
                    <br />
                    by Streamr&apos;s P2P network and companion blockchain settlement layer.
                </Topic.Paragraph>
            </Container>
            <Stops current={0} size={4}>
                {({ size, current, setStop }) => (
                    <Swipeable
                        onSwipedLeft={() => setStop(Math.min(size - 1, current + 1))}
                        onSwipedRight={() => setStop(Math.max(0, current - 1))}
                    >
                        <Apps
                            style={{
                                transform: `translateX(calc(-${current * 280}px - ${current * 1.5}rem))`,
                            }}
                        >
                            <FeatureCard $appear icon="dataunions" title="Data Unions" to={urls.docs.dataUnions}>
                                <p>
                                    Framework to incentivize the
                                    <br />
                                    creation of crowdsourced data.
                                </p>
                                <p>
                                    <Link href={urls.docs.dataUnions} noDecoration>
                                        Learn more
                                    </Link>
                                </p>
                            </FeatureCard>
                            <FeatureCard $appear icon="marketplace" title="Marketplace" to={urls.docs.marketplace}>
                                <p>
                                    Monetize your real-time data.
                                    <br />
                                    Build and publish in Core.
                                </p>
                                <p>
                                    <Link to={urls.docs.marketplace} noDecoration>
                                        Learn more
                                    </Link>
                                    <Bullet />
                                    <ExternalLink href={urls.apps.marketplace}>
                                        Visit app
                                    </ExternalLink>
                                </p>
                            </FeatureCard>
                            <FeatureCard $appear icon="core" title="Core app" to={urls.docs.core}>
                                <p>
                                    Connect your wallet to create
                                    <br />
                                    and manage your streams.
                                </p>
                                <p>
                                    <Link to={urls.docs.core} noDecoration>
                                        Learn more
                                    </Link>
                                    <Bullet />
                                    <ExternalLink href={urls.apps.core}>
                                        Visit app
                                    </ExternalLink>
                                </p>
                            </FeatureCard>
                            <FeatureCard $appear icon="networkexplorer" title="Network Explorer" to={urls.apps.networkExplorer}>
                                <p>
                                    Explore live metrics for nodes
                                    <br />
                                    and streams on the Network.
                                </p>
                                <p>
                                    <ExternalLink href={urls.apps.networkExplorer}>
                                        Visit app
                                    </ExternalLink>
                                </p>
                            </FeatureCard>
                        </Apps>
                        <Display as={Spacer} head="4rem" mobile="block" tablet="none">
                            <SliderNav
                                size={4}
                                current={current}
                                onChange={setStop}
                                theme={SliderNav.DarkBlueTheme}
                            />
                        </Display>
                    </Swipeable>
                )}
            </Stops>
        </Topic.Body>
    </div>
)

const ApplicationLayer = styled(UnstyledApplicationLayer)`
    ${Topic.Title} {
        margin: 0 2rem;
    }

    ${Topic.Paragraph} {
        display: none;
        margin: 0;
    }

    @media (min-width: ${TABLET}px) {
        ${Topic.Title} {
            margin: 0;
        }

        ${Topic.Paragraph} {
            margin-top: 1.5rem;
            display: block;
            text-align: center;
            font-size: 1rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        ${Topic.Paragraph} {
            margin-top: 2.5rem;
            font-size: 1.125rem;
        }
    }
`

export default ApplicationLayer
